import {
  createRouter as cr,
  createWebHashHistory,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";

import { useApi } from "@/services/api";
import { useToastsStore } from "@/stores/toasts";
import DeviceTokenView from "@/views/authentication/DeviceTokenView.vue";
import LoginSuccessView from "@/views/authentication/LoginSuccessView.vue";
import LoginView from "@/views/authentication/LoginView.vue";
import LogoutView from "@/views/authentication/LogoutView.vue";
import EstimatorReportComparisonBuilderView from "@/views/comparison/EstimatorReportComparisonBuilderView.vue";
import EstimatorReportComparisonView from "@/views/comparison/EstimatorReportComparisonView.vue";
import CrashView from "@/views/CrashView.vue";
import DispatchView from "@/views/dispatch/DispatchView.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import ProjectDetailView from "@/views/projects/ProjectDetailView.vue";
import ProjectsListView from "@/views/projects/ProjectsListView.vue";
import EstimatorReportView from "@/views/reports/EstimatorReportView.vue";
import TenantDetailView from "@/views/tenants/TenantDetailView.vue";

declare module "vue-router" {
  interface RouteMeta {
    requiresAuthentication: boolean;
  }
}

export enum RouteNames {
  Dispatch = "dispatch",
  Projects = "projects",
  ProjectDetail = "projectdetail",
  EstimatorReportDetail = "estimator_report_detail",
  ComparisonBuilder = "comparison_builder",
  Comparison = "comparison",
  TenantDetail = "tenant_detail",
  Login = "login",
  LoginSuccess = "login_success",
  DeviceToken = "device_token",
  Logout = "logout",
  NotFound = "not_found",
  Crash = "crash",
  Components = "components",
}

export enum ProjectDetailSections {
  Reports = "reports",
  Comparisons = "comparisons",
  Presentations = "presentations",
}

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: RouteNames.Dispatch,
    component: DispatchView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:tenantName",
    name: RouteNames.Projects,
    component: ProjectsListView,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/:tenantName/:projectName/:section?/:itemId?",
    name: RouteNames.ProjectDetail,
    component: ProjectDetailView,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/:tenantName/:projectName/reports/:reportId",
    name: RouteNames.EstimatorReportDetail,
    component: EstimatorReportView,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/:tenantName/:projectName/comparisons/builder",
    name: RouteNames.ComparisonBuilder,
    component: EstimatorReportComparisonBuilderView,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/:tenantName/:projectName/comparisons/:comparisonId",
    name: RouteNames.Comparison,
    component: EstimatorReportComparisonView,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/tenant/:tenantId",
    name: RouteNames.TenantDetail,
    component: TenantDetailView,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/login",
    name: RouteNames.Login,
    component: LoginView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/login/success",
    name: RouteNames.LoginSuccess,
    component: LoginSuccessView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/device",
    name: RouteNames.DeviceToken,
    component: DeviceTokenView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/logout",
    name: RouteNames.Logout,
    component: LogoutView,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/not-found",
    name: RouteNames.NotFound,
    component: NotFoundView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/crash",
    name: RouteNames.Crash,
    component: CrashView,
    meta: {
      requiresAuthentication: false,
    },
  },
];

if (import.meta.env.DEV) {
  routes.push({
    path: "/components",
    name: RouteNames.Components,
    component: () => import("@/views/components/ComponentsView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  });
}

function createRouter() {
  const api = useApi();
  /**
   * To handle PR deployment in the same bucket
   * we use anchor navigation if the base is not empty.
   */
  const history =
    import.meta.env.BASE_URL !== "/"
      ? createWebHashHistory(import.meta.env.BASE_URL)
      : createWebHistory(import.meta.env.BASE_URL);
  const router = cr({
    history,
    routes,
  });
  router.beforeEach(async (to) => {
    // refresh the token if needed
    if ("error" in to.query) {
      const toastsStore = useToastsStore();
      switch (to.query["error"]) {
        case "invalid_state_exception": {
          toastsStore.addToast(
            "Something went wrong during authentication, please try again.",
            "error"
          );
          break;
        }
        default: {
          break;
        }
      }
    }
    const isUserAuthenticated = await api.isUserAuthenticated();
    if (to.meta.requiresAuthentication && !isUserAuthenticated) {
      return { name: RouteNames.Dispatch };
    }
  });
  return router;
}

export const router = createRouter();
